import notification from "@app/services/notification";

/** Окно дедупликации повторного репорта (мс). */
export const APP_ERROR_DEDUPE_WINDOW_MS = 1200;

const DEFAULT_NOTIFICATION_DURATION_MS = 10_000;

const objectReportTimes = new WeakMap<object, number>();
const keyReportTimes = new Map<string, number>();

/**
 * Опции репорта ошибки приложения.
 */
export interface AppErrorReportOptions {
    /** Источник ошибки для лога и `fromId` уведомления. */
    from?: string;
    /** Дополнительный контекст для `console.error`. */
    context?: unknown;
    /** Ключ дедупликации поверх объекта ошибки. */
    dedupeKey?: string;
    /** Длительность toast (мс). */
    duration?: number;
}

function pruneExpiredKeys(now: number): void {
    for (const [key, reportedAt] of keyReportTimes) {
        if (now - reportedAt >= APP_ERROR_DEDUPE_WINDOW_MS) {
            keyReportTimes.delete(key);
        }
    }
}

function shouldReportByObject(error: unknown, now: number): boolean {
    if (error == null || typeof error !== "object") {
        return true;
    }

    const reportedAt = objectReportTimes.get(error);
    if (reportedAt != null && now - reportedAt < APP_ERROR_DEDUPE_WINDOW_MS) {
        return false;
    }

    objectReportTimes.set(error, now);
    return true;
}

function shouldReportByKey(dedupeKey: string | undefined, now: number): boolean {
    if (dedupeKey == null) {
        return true;
    }

    const reportedAt = keyReportTimes.get(dedupeKey);
    if (reportedAt != null && now - reportedAt < APP_ERROR_DEDUPE_WINDOW_MS) {
        return false;
    }

    keyReportTimes.set(dedupeKey, now);
    return true;
}

function normalizeUnknownError(error: unknown): Error {
    if (error instanceof Error) {
        return error;
    }

    if (typeof error === "string") {
        return new Error(error);
    }

    try {
        const serialized = JSON.stringify(error, null, 2);
        return new Error(serialized ?? String(error));
    } catch {
        return new Error(String(error));
    }
}

function stringifyContext(context: unknown): string {
    if (context == null) {
        return "—";
    }

    try {
        return JSON.stringify(context, null, 2) ?? describeUnknownContext(context);
    } catch {
        return describeUnknownContext(context);
    }
}

function describeUnknownContext(context: unknown): string {
    if (typeof context === "object") {
        return Object.prototype.toString.call(context);
    }
    if (typeof context === "string") {
        return context;
    }
    if (typeof context === "number" || typeof context === "boolean" || typeof context === "bigint") {
        return `${context}`;
    }
    if (typeof context === "symbol") {
        return context.description != null ? `Symbol(${context.description})` : "Symbol()";
    }
    if (typeof context === "function") {
        const fnName = context.name.length > 0 ? context.name : "anonymous";
        return `[Function ${fnName}]`;
    }
    return "unknown";
}

/**
 * Репортит ошибку: `console.error` + `notification.error` с дедупликацией.
 */
export const appErrorReporter = {
    report(error: unknown, options: AppErrorReportOptions = {}): void {
        const now = Date.now();
        pruneExpiredKeys(now);

        if (!shouldReportByKey(options.dedupeKey, now)) {
            return;
        }

        if (!shouldReportByObject(error, now)) {
            return;
        }

        const normalizedError = normalizeUnknownError(error);
        const from = options.from ?? "<не указан>";
        const context = stringifyContext(options.context);

        console.error(
            `[APP ERROR] from: ${from}\ncontext: ${context}\nmessage: ${normalizedError.message}`,
            normalizedError,
        );

        notification.error(normalizedError.message, {
            fromId: options.from,
            duration: options.duration ?? DEFAULT_NOTIFICATION_DURATION_MS,
        });
    },
};

/**
 * Короткий API репорта ошибок для пользовательского кода.
 */
export function reportError(error: unknown, options: AppErrorReportOptions = {}): void {
    appErrorReporter.report(error, options);
}

export default appErrorReporter;
